import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import WealthPlanOptimizerContent from "../../../WealthPlanOptimizer/WealthPlanOptimizerContent";
import {wealthPOApiClient} from "../../../WealthPlanOptimizer/WealthPOApiClient";
import {RouteWithId} from "../../../routes/types";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {
    initialStateOfResidencyStrategy,
    StateInputDto,
    StrategyDetailsSectionInfo,
    StrategyType,
    WealthPOSummaryResponse
} from "../../../WealthPlanOptimizer/WealthPOTypes";
import {
    buildEstateTaxBreakdownReportContent,
    buildEstateTaxBreakdownRowsList,
    buildGTGTExemptionsList,
    buildStrategyDetailsList,
} from "../../../WealthPlanOptimizer/WealthPOUtils";
import {
    resetWealthPlanOptimizerState,
    setWealthPlanOptimizerState,
    WealthPlanOptimizerState
} from "../../../WealthPlanOptimizer/WealthPlanOptimizerSlice";
import LoadingIndicator from "../../../pages/LoadingIndicator";
import {NO_OP} from "../../../constants/common";
import PrintViewWrapper from "../PrintViewWrapper";
import GenericErrorModal, {
    genericEmptyErrorModalData,
    GenericErrorModalData
} from "../../../components/Modal/Error/GenericErrorModal";
import {EstateTaxBreakdownReportRowProps} from "./EstateTaxBreakdownReportRow";
import {v4 as randomUuid} from "uuid";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";

interface WealthPlanOptimizerWrapperProps {
    displayName: string;
}

const WealthPlanOptimizerWrapper = ({displayName}: WealthPlanOptimizerWrapperProps) => {
    const {id} = useParams<RouteWithId>();
    const dispatch = useAppDispatch();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [genericError, setGenericError] = React.useState<GenericErrorModalData>(genericEmptyErrorModalData);
    const [listOfStates, setListOfStates] = useState([] as StateInputDto[]);
    const [listOfStrategyDetailsList, setListOfStrategyDetailsList] = useState<StrategyDetailsSectionInfo[][]>([]);
    const [listOfEstateTaxBreakdownRows, setListOfEstateTaxBreakdownRows] = useState<EstateTaxBreakdownReportRowProps[][]>([]);
    const [wealthPOSummaryResponse, setWealthPOSummaryResponse] = useState<WealthPOSummaryResponse>({} as WealthPOSummaryResponse);
    const enableEstateTaxGraduatedRates = useAppSelector(selectReleaseToggles)?.enableEstateTaxGraduatedRates;

    const openErrorModal = (error: Error | any) => {
        setGenericError({
            isOpen: true,
            header: "Unable to Load This Page",
            message: (
                <>
                    <p>Check your VPN connection and retry.</p>
                    <p>If the problem persists, contact <a href="mailto:GPIITSupport@ntrs.com">GPS Technical
                        Support</a> at GPIITSupport@ntrs.com and provide the following details:</p>
                </>
            ),
            profileId: id,
            time: new Date(),
            errorDetail: `Failed to load Wealth Plan Optimizer (${error.status})`,
            operationId: error?.headers?.get?.('trace-id') ?? ''
        });
    };

    useEffect(() => {
        let isActiveRequest = true;
        setIsLoading(true);
        Promise.all([
            wealthPOApiClient.getWealthPOSummary(id),
            wealthPOApiClient.getStateEstateTransferRatesReferenceData(id)
        ]).then(([wealthPOSummaryResponse,
                     stateEstateTransferRates]) => {
            if (!isActiveRequest) {
                return;
            }

            setGenericError(genericEmptyErrorModalData);

            if (wealthPOSummaryResponse === null) {
                setListOfStrategyDetailsList([]);
                setListOfEstateTaxBreakdownRows([]);
                setListOfStates([]);
                setWealthPOSummaryResponse({} as WealthPOSummaryResponse);
                dispatch(resetWealthPlanOptimizerState());
                setIsLoading(false);
                return
            }

            if (wealthPOSummaryResponse.stateOfResidencyStrategy === null) {
                wealthPOSummaryResponse.stateOfResidencyStrategy = initialStateOfResidencyStrategy;
            }

            dispatch(setWealthPlanOptimizerState({
                wealthPOSummaryResponse,
            } as WealthPlanOptimizerState))
            const {strategyDetailsList, listOfStrategyDetailsLists} = buildStrategyDetailsList(
                2,
                null,
                wealthPOSummaryResponse.annualGiftingStrategies,
                wealthPOSummaryResponse.giftToGrantorTrustStrategies,
                wealthPOSummaryResponse.gratStrategies
            );

            const estateTaxBreakdownRows = buildEstateTaxBreakdownRowsList(
                wealthPOSummaryResponse.estateTaxBreakdownWithoutStrategies,
                wealthPOSummaryResponse.estateTaxBreakdownWithStrategies,
                enableEstateTaxGraduatedRates,
                strategyDetailsList.filter(strategy => strategy.enabled && strategy.type !== StrategyType.STATE_OF_RESIDENCY),
                wealthPOSummaryResponse.stateOfResidencyStrategy.enabled ? wealthPOSummaryResponse.stateOfResidencyStrategy.description : "",
                buildGTGTExemptionsList(wealthPOSummaryResponse.giftToGrantorTrustStrategies)
            );

            setListOfStrategyDetailsList(listOfStrategyDetailsLists);
            setListOfEstateTaxBreakdownRows(estateTaxBreakdownRows);
            setListOfStates(stateEstateTransferRates.stateInputsDTOs);
            setWealthPOSummaryResponse(wealthPOSummaryResponse)
            setIsLoading(false);
        }).catch((error) => {
            if (!isActiveRequest) {
                return;
            }

            openErrorModal(error);
            setIsLoading(false);
        })

        return () => {
            isActiveRequest = false;
            setListOfStrategyDetailsList([]);
            dispatch(resetWealthPlanOptimizerState());
        }
    }, [id])

    const memoizedList = useMemo(() => {
        if (genericError.isOpen) {
            return (
                <div data-testid="report-generation-blocking-error" className="report-generation-blocking-error">
                    <GenericErrorModal
                        errorModalData={genericError}
                        onClickButton={NO_OP}
                        onRequestClose={NO_OP}
                        buttonText="Retry"
                        buttonProps={
                            {
                                primary: true,
                                className: 'full-width center-align',
                                iconPosition: 'left',
                                iconName: 'refresh'
                            }
                        }
                        showAlertIcon={false}
                    />
                </div>
            );
        } else if (listOfStrategyDetailsList.length !== 0 || listOfEstateTaxBreakdownRows.length !== 0) {
            const strategyListPages = listOfStrategyDetailsList.map((strategyList, index) =>
                <div key={`strategyList-${randomUuid()}`}>
                    <PrintViewWrapper displayName={displayName}>
                        <WealthPlanOptimizerContent refreshStrategiesSummary={NO_OP}
                                                    strategyDetailsList={strategyList}
                                                    isContinued={index > 0}
                                                    isInReportPreview={true}
                                                    listOfStates={listOfStates}
                                                    wealthPOSummaryResponse={wealthPOSummaryResponse}
                        />
                    </PrintViewWrapper>
                </div>);

            const strategyBreakdownPages = listOfEstateTaxBreakdownRows.map((strategyBreakdown, index) =>
                <div key={`strategyBreakdown-${randomUuid()}`}>
                    <PrintViewWrapper displayName={displayName}>
                        <WealthPlanOptimizerContent refreshStrategiesSummary={NO_OP}
                                                    strategyDetailsList={[]}
                                                    isContinued={true}
                                                    isInReportPreview={true}
                                                    listOfStates={listOfStates}
                                                    wealthPOSummaryResponse={wealthPOSummaryResponse}
                                                    estateTaxBreakdownReportContent={
                                                        buildEstateTaxBreakdownReportContent(strategyBreakdown, index > 0)
                                                    }
                        />
                    </PrintViewWrapper>
                </div>);

            return strategyListPages.concat(strategyBreakdownPages);
        } else {
            return (
                <div>
                    <PrintViewWrapper displayName={displayName}>
                        <WealthPlanOptimizerContent refreshStrategiesSummary={NO_OP}
                                                    strategyDetailsList={[]}
                                                    isContinued={false}
                                                    isInReportPreview={true}
                                                    listOfStates={listOfStates}
                                                    wealthPOSummaryResponse={wealthPOSummaryResponse}
                        />
                    </PrintViewWrapper>
                </div>
            )
        }
    }, [listOfStrategyDetailsList, listOfEstateTaxBreakdownRows, genericError.isOpen, listOfStates, wealthPOSummaryResponse]);

    if (isLoading) {
        return <LoadingIndicator/>
    }

    return (
        <>
            {
                memoizedList
            }
        </>
    )
}

export default WealthPlanOptimizerWrapper;
